import axios from 'axios';
import { API_CONFIG, STORAGE_KEYS, ERROR_MESSAGES, ROUTES } from './constants.js';

// axios 인스턴스 생성
const apiClient = axios.create({
  baseURL: import.meta.env.DEV ? '' : API_CONFIG.BASE_URL, // 개발 환경에서는 vite 프록시 사용
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 네트워크 모니터링을 위한 전역 함수 등록
let networkMonitorCallback = null;

export const registerNetworkMonitor = (callback) => {
  networkMonitorCallback = callback;
};

export const unregisterNetworkMonitor = () => {
  networkMonitorCallback = null;
};

// 요청 인터셉터
apiClient.interceptors.request.use(
  (config) => {
    // 인증 토큰 추가
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // FormData를 사용하는 경우 Content-Type 헤더를 제거하여
    // axios가 자동으로 multipart/form-data와 boundary를 설정하도록 함
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    // 네트워크 모니터링: 요청 시작
    if (networkMonitorCallback) {
      const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      config.metadata = { requestId, startTime: Date.now() };
      
      const requestData = {
        id: requestId,
        method: config.method?.toUpperCase() || 'GET',
        url: config.url || '',
        fullUrl: (config.baseURL || '') + (config.url || ''),
        timestamp: Date.now(),
        status: 'pending',
        requestHeaders: { ...config.headers },
        requestBody: config.data,
      };
      
      networkMonitorCallback('request', requestData);
    }
    
    return config;
  },
  (error) => {
    console.error('Request Error:', error);
    
    // 네트워크 모니터링: 요청 에러
    if (networkMonitorCallback && error.config?.metadata) {
      const { requestId } = error.config.metadata;
      networkMonitorCallback('error', {
        id: requestId,
        error: {
          message: error.message,
          code: error.code,
        },
        status: 'error',
      });
    }
    
    return Promise.reject(error);
  }
);

// 응답 인터셉터
apiClient.interceptors.response.use(
  (response) => {
    // 네트워크 모니터링: 성공 응답
    if (networkMonitorCallback && response.config?.metadata) {
      const { requestId, startTime } = response.config.metadata;
      const duration = Date.now() - startTime;
      const responseSize = JSON.stringify(response.data).length;
      
      networkMonitorCallback('response', {
        id: requestId,
        statusCode: response.status,
        responseHeaders: { ...response.headers },
        responseData: response.data,
        duration,
        responseSize,
        status: 'completed',
      });
    }
    
    return response;
  },
  (error) => {
    console.error('Response Error:', error);
    
    // 네트워크 모니터링: 에러 응답
    if (networkMonitorCallback && error.config?.metadata) {
      const { requestId, startTime } = error.config.metadata;
      const duration = Date.now() - startTime;
      
      const responseData = {
        id: requestId,
        duration,
        status: 'error',
      };
      
      if (error.response) {
        responseData.statusCode = error.response.status;
        responseData.responseHeaders = { ...error.response.headers };
        responseData.responseData = error.response.data;
        responseData.responseSize = JSON.stringify(error.response.data || {}).length;
        responseData.error = {
          message: error.message,
          data: error.response.data,
        };
      } else if (error.request) {
        responseData.error = {
          message: 'Network Error',
          code: 'NETWORK_ERROR',
        };
      } else {
        responseData.error = {
          message: error.message,
        };
      }
      
      networkMonitorCallback('error', responseData);
    }
    
    // 에러 처리
    if (error.response) {
      const { status, data, headers } = error.response;
      
      // 401 에러 시 상세 정보 로깅
      if (status === 401) {
        const wwwAuthenticate = headers['www-authenticate'] || headers['WWW-Authenticate'];
        if (wwwAuthenticate) {
          
          // Bearer error 파싱
          const errorMatch = wwwAuthenticate.match(/error="([^"]+)"/);
          const errorDescMatch = wwwAuthenticate.match(/error_description="([^"]+)"/);
          
          if (errorMatch || errorDescMatch) {
            console.error('인증 오류:', {
              error: errorMatch ? errorMatch[1] : 'unknown',
              description: errorDescMatch ? errorDescMatch[1] : 'unknown'
            });
          }
        }
        
        // 토큰 정보 확인
        const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (token) {
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            console.error('현재 토큰의 audience:', payload.aud);
            console.error('현재 토큰의 issuer:', payload.iss);
          } catch (e) {
            console.error('토큰 파싱 실패:', e);
          }
        }
      }
      
      switch (status) {
        case 401:
          // 인증 실패 시 토큰 제거 (모든 토큰 키 확인)
          localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.USER_DATA);
          
          // 로그인 페이지로 리다이렉트 (현재 로그인 페이지가 아닌 경우에만)
          if (window.location.pathname !== ROUTES.LOGIN) {
            window.location.href = ROUTES.LOGIN;
          }
          break;
        case 403:
          console.error('접근 권한이 없습니다.');
          break;
        case 404:
          console.error('요청한 리소스를 찾을 수 없습니다.');
          break;
        case 500:
          console.error('서버 오류가 발생했습니다.');
          break;
        default:
          console.error('API 오류:', data?.message || ERROR_MESSAGES.UNKNOWN_ERROR);
      }
    } else if (error.request) {
      console.error(ERROR_MESSAGES.NETWORK_ERROR);
    } else {
      console.error('요청 설정 오류:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// 기본 API 함수들
export const api = {
  // GET 요청
  get: async (url, config = {}) => {
    try {
      const response = await apiClient.get(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // POST 요청
  post: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.post(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // PUT 요청
  put: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.put(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // PATCH 요청
  patch: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.patch(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // DELETE 요청
  delete: async (url, config = {}) => {
    try {
      const response = await apiClient.delete(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // 파일 업로드
  upload: async (url, file, config = {}) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await apiClient.post(url, formData, {
        ...config,
        headers: {
          'Content-Type': 'multipart/form-data',
          ...config.headers,
        },
      });
      
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // 다중 파일 업로드
  uploadMultiple: async (url, files, config = {}) => {
    try {
      const formData = new FormData();
      files.forEach((file, index) => {
        formData.append(`files[${index}]`, file);
      });
      
      const response = await apiClient.post(url, formData, {
        ...config,
        headers: {
          'Content-Type': 'multipart/form-data',
          ...config.headers,
        },
      });
      
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// 재시도 로직이 포함된 API 함수
export const apiWithRetry = {
  get: async (url, config = {}, retryCount = API_CONFIG.RETRY_ATTEMPTS) => {
    for (let i = 0; i < retryCount; i++) {
      try {
        return await api.get(url, config);
      } catch (error) {
        if (i === retryCount - 1) throw error;
        await new Promise(resolve => setTimeout(resolve, API_CONFIG.RETRY_DELAY));
      }
    }
  },

  post: async (url, data = {}, config = {}, retryCount = API_CONFIG.RETRY_ATTEMPTS) => {
    for (let i = 0; i < retryCount; i++) {
      try {
        return await api.post(url, data, config);
      } catch (error) {
        if (i === retryCount - 1) throw error;
        await new Promise(resolve => setTimeout(resolve, API_CONFIG.RETRY_DELAY));
      }
    }
  },
};

export default api; 